'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import {
  Avatar,
  getInitialsFromName,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogClose,
  VisuallyHidden,
  Popover,
  PopoverTrigger,
  PopoverContent
} from '@goodneighbor/ui';
import {
  Menu01,
  X,
  HomeLine,
  User01,
  Star01,
  Settings02,
  LifeBuoy02
} from '@goodneighbor/ui/icons/untitled';
import useAccountQuery from '@/hooks/useAccountQuery';
import { useHasJWT } from '@/hooks/useJWT';
import { signOut } from '@/services/auth';
import { useQueryClient } from '@tanstack/react-query';
import styles from './campaignHeader.module.scss';

const CAMPAIGN_NAV_LINKS = [
  {
    label: 'Volunteer',
    href: 'https://goodneighbordayamerica.com/volunteer'
  },
  {
    label: 'Partner',
    href: 'https://goodneighbordayamerica.com/partner'
  }
  // {
  //   label: 'Sponsor',
  //   href: 'https://goodneighbordayamerica.com/sponsor'
  // }
];

export default function CampaignHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const isPartnerPage = pathname?.startsWith('/partner') ?? false;
  const queryClient = useQueryClient();
  const hasJWT = useHasJWT();
  const account = useAccountQuery({
    enabled: hasJWT
  });

  const isAuthenticated = hasJWT && !!account.data?.data;
  const accountData = account.data?.data;
  const isPartner =
    !!accountData?.organization && accountData.organization.status === 'ACTIVE';
  const fullName = accountData
    ? `${accountData.firstName} ${accountData.lastName}`
    : undefined;
  const initials = getInitialsFromName(fullName);

  const handleSignOut = () => {
    signOut({
      queryClient,
      redirectUrl: isPartner ? '/partner' : '/volunteer'
    });
  };

  return (
    <header className={styles.campaignHeader}>
      <div className={styles.container}>
        <div className={styles.logoWrapper}>
          {isPartnerPage ? (
            <a
              href="https://www.goodneighbordayamerica.com"
              className={styles.logoLink}
            >
              <img
                src="/icons/gnda-logo_dark.svg"
                alt="Good Neighbor Day America"
                className={styles.logo}
              />
            </a>
          ) : (
            <Link href="/" className={styles.logoLink}>
              <img
                src="/icons/gnda-logo_dark.svg"
                alt="Good Neighbor Day America"
                className={styles.logo}
              />
            </Link>
          )}
        </div>

        <nav className={styles.nav}>
          <ul className={styles.navList}>
            {CAMPAIGN_NAV_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  className={styles.navLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          {isAuthenticated ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={styles.avatarButton}
                  aria-label="Account menu"
                >
                  <Avatar
                    alt={fullName}
                    initials={initials}
                    src={accountData?.profile?.avatarUrl}
                    size="md"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className={styles.accountMenu}>
                <div className={styles.accountMenuHeader}>
                  <span className={styles.accountName}>{fullName}</span>
                  <span className={styles.accountEmail}>
                    {accountData?.email}
                  </span>
                </div>
                <div className={styles.accountMenuDivider} />
                {isPartner ? (
                  <Link
                    href="/partner/dashboard"
                    className={styles.accountMenuItem}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/volunteer/account"
                      className={styles.accountMenuItem}
                    >
                      Account
                    </Link>
                    <Link
                      href="/volunteer/my-events"
                      className={styles.accountMenuItem}
                    >
                      My Events
                    </Link>
                  </>
                )}
                <button
                  onClick={handleSignOut}
                  className={styles.accountMenuItem}
                >
                  Sign out
                </button>
              </PopoverContent>
            </Popover>
          ) : (
            <div className={styles.authButtons}>
              <Button
                href={isPartnerPage ? '/partner' : '/volunteer'}
                color="tertiary"
                size="sm"
                className={styles.loginButton}
              >
                Log in
              </Button>
              <Button
                href={isPartnerPage ? '/partner/sign-up' : '/volunteer/sign-up'}
                color="primary"
                size="sm"
                className={styles.signUpButton}
              >
                Sign up
              </Button>
            </div>
          )}
        </div>

        {/* Mobile hamburger menu */}
        <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DialogTrigger asChild>
            <button
              className={styles.hamburgerButton}
              aria-label="Open navigation menu"
            >
              <Menu01 />
            </button>
          </DialogTrigger>
          <DialogContent
            className={styles.mobileMenu}
            showOverlay={false}
            dismissable={false}
            style={{
              position: 'fixed',
              inset: 0,
              transform: 'none',
              translate: 'none',
              width: '100vw',
              height: '100vh',
              maxWidth: 'none',
              maxHeight: 'none',
              padding: 0,
              border: 'none',
              borderRadius: 0,
              gap: 0,
              backgroundColor: 'white'
            }}
          >
            <VisuallyHidden>
              <DialogTitle>Navigation Menu</DialogTitle>
            </VisuallyHidden>
            <div className={styles.mobileMenuHeader}>
              {isPartnerPage ? (
                <a
                  href="https://www.goodneighbordayamerica.com"
                  className={styles.logoLink}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img
                    src="/icons/gnda-logo_dark.svg"
                    alt="Good Neighbor Day America"
                    className={styles.logo}
                  />
                </a>
              ) : (
                <Link
                  href="/"
                  className={styles.logoLink}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img
                    src="/icons/gnda-logo_dark.svg"
                    alt="Good Neighbor Day America"
                    className={styles.logo}
                  />
                </Link>
              )}
              <DialogClose asChild>
                <button
                  className={styles.closeButton}
                  aria-label="Close navigation menu"
                >
                  <X />
                </button>
              </DialogClose>
            </div>
            <nav className={styles.mobileNav}>
              <ul className={styles.mobileNavList}>
                {CAMPAIGN_NAV_LINKS.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className={styles.mobileNavLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
              {isAuthenticated && (
                <>
                  <div className={styles.mobileNavDivider} />
                  <ul className={styles.mobileNavList}>
                    {!isPartner && (
                      <>
                        <li>
                          <Link
                            href="/volunteer/account"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Settings02 className={styles.mobileNavIcon} />
                            Account
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/volunteer/my-events"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Star01 className={styles.mobileNavIcon} />
                            My Events
                          </Link>
                        </li>
                      </>
                    )}
                    {isPartner && (
                      <>
                        <li>
                          <Link
                            href="/partner/dashboard"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <HomeLine className={styles.mobileNavIcon} />
                            Home
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/partner/dashboard/volunteers"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <User01 className={styles.mobileNavIcon} />
                            Volunteers
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/partner/dashboard/events"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Star01 className={styles.mobileNavIcon} />
                            Events
                          </Link>
                        </li>
                        <li>
                          <Link
                            href="/partner/dashboard/account"
                            className={styles.mobileNavLinkWithIcon}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Settings02 className={styles.mobileNavIcon} />
                            Account
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>
                </>
              )}
              {isAuthenticated ? (
                <div className={styles.mobileAuthButtons}>
                  {isPartner && (
                    <>
                      <a
                        href="mailto:partner@goodneighbordayamerica.com"
                        className={styles.mobileNavLinkWithIcon}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <LifeBuoy02 className={styles.mobileNavIcon} />
                        Support
                      </a>
                      <div className={styles.mobileNavDivider} />
                    </>
                  )}
                  <div className={styles.mobileAccountInfo}>
                    <Avatar
                      alt={fullName}
                      initials={initials}
                      src={accountData?.profile?.avatarUrl}
                      size="md"
                    />
                    <div className={styles.mobileAccountDetails}>
                      <span className={styles.mobileAccountName}>
                        {fullName}
                      </span>
                      <span className={styles.mobileAccountEmail}>
                        {accountData?.email}
                      </span>
                    </div>
                  </div>
                  <Button
                    color="tertiary"
                    size="lg"
                    className={styles.mobileAuthButton}
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSignOut();
                    }}
                  >
                    Sign out
                  </Button>
                </div>
              ) : (
                <div className={styles.mobileAuthButtons}>
                  <Button
                    href={isPartnerPage ? '/partner' : '/volunteer'}
                    color="tertiary"
                    size="lg"
                    className={styles.mobileAuthButton}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Log in
                  </Button>
                  <Button
                    href={
                      isPartnerPage ? '/partner/sign-up' : '/volunteer/sign-up'
                    }
                    color="primary"
                    size="lg"
                    className={styles.mobileAuthButton}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign up
                  </Button>
                </div>
              )}
            </nav>
          </DialogContent>
        </Dialog>
      </div>
    </header>
  );
}
